<template>
  <!-- Loading Auth State -->
  <v-app v-if="loadingAuth">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-row>
    </v-container>
  </v-app>

  <!-- Login Screen -->
  <v-app v-else-if="!isAuthenticated">
    <Login />
  </v-app>

  <!-- NIC Setup (local mode bypasses Login, so we guard here too) -->
  <v-app v-else-if="!nicConfigured">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="5">
          <v-card class="elevation-12">
            <v-toolbar color="primary">
              <v-toolbar-title>Network Setup</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <NicSetup @done="nicConfigured = true" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>

  <!-- Pending Approval Screen -->
  <v-app v-else-if="currentUser?.role === 0">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-card class="text-center pa-8" elevation="4">
          <v-icon icon="mdi-account-clock" size="64" color="warning" class="mb-4"></v-icon>
          <div class="text-h5 font-weight-bold mb-2">Account Pending Approval</div>
          <div class="text-body-1 mb-4">
            Your account is waiting for administrator approval.<br>
            Please contact an admin to verify your access.
          </div>
          <div class="text-caption text-medium-emphasis mb-6">
            ID: {{ currentUser.id }}<br>
            Username: {{ currentUser.username }}
          </div>
          <v-btn color="primary" href="/auth/logout" prepend-icon="mdi-logout">Logout</v-btn>
        </v-card>
      </v-row>
    </v-container>
  </v-app>

  <!-- Main App -->
  <div v-else>
    <v-sheet width="100vw" class="d-flex flex-wrap pl-2 pr-2 py-1 align-center">
        <v-sheet class="d-flex align-center flex-grow-1" style="overflow: hidden;">
            <span class="font-weight-bold mr-2 text-no-wrap">funnycharts
                <v-icon v-if="socketConnected" icon="mdi-check-circle" color="success" size="small" class="ml-1" />
                <v-icon v-else icon="mdi-close-circle" color="error" size="small" class="ml-1" />
            </span>

            <v-divider vertical inset class="mx-2"></v-divider>

            <!-- User Info -->
            <v-avatar size="24" class="mr-2">
                <v-img :src="currentUser?.avatar ? `https://cdn.discordapp.com/avatars/${currentUser.id}/${currentUser.avatar}.png` : ''"></v-img>
            </v-avatar>
            <span class="text-body-2 mr-2 hidden-sm-and-down">{{ currentUser?.username }}</span>
            <v-chip size="x-small" color="primary" variant="outlined" class="mr-2 hidden-sm-and-down">
                {{ getRoleName(currentUser?.role) }}
            </v-chip>

            <v-divider vertical inset class="mx-2"></v-divider>

            <!-- Resource Loading -->
            <transition name="fade">
                <div v-if="!resourcesReady" class="d-flex align-center ml-2 text-caption text-warning" style="gap: 8px;">
                    <v-progress-circular indeterminate color="warning" size="16" width="2"></v-progress-circular>
                    <span class="hidden-xs">Loading Resources... ({{ resourceLoadingProgress }})</span>
                </div>
            </transition>
        </v-sheet>

        <!-- Actions -->
        <v-sheet class="d-flex align-center">
             <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                    <v-btn @click="loadFromFile" v-bind="props" :disabled="!resourcesReady || isLoading"
                        :loading="isLoading" color="primary" size="small" variant="text" icon="mdi-upload">
                    </v-btn>
                </template>
                Load from file
            </v-tooltip>

             <v-tooltip location="bottom" v-if="(currentUser?.role || 0) >= 2">
                <template v-slot:activator="{ props }">
                    <v-btn @click="download" v-bind="props" :loading="isLoading" color="primary" size="small" variant="text" icon="mdi-download">
                    </v-btn>
                </template>
                Download Log
            </v-tooltip>
            
            <v-btn @click="anonymizeMode = !anonymizeMode"
                :color="anonymizeMode ? 'error' : 'primary'" size="small" variant="text"
                :icon="anonymizeMode ? 'mdi-eye-off' : 'mdi-eye'">
            </v-btn>

            <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                    <v-btn @click="loadFromServer" v-bind="props" :loading="isLoading" color="primary" size="small" variant="text" icon="mdi-refresh">
                    </v-btn>
                </template>
                Reload from Server
            </v-tooltip>

            <v-tooltip location="bottom" v-if="(currentUser?.role || 0) >= 3">
                <template v-slot:activator="{ props }">
                    <v-btn @click="showCaptureSettings = true" v-bind="props" color="primary" size="small" variant="text" icon="mdi-cog">
                    </v-btn>
                </template>
                Capture Settings
            </v-tooltip>

            <!--
            <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                    <v-btn @click="clearData" v-bind="props" :loading="isLoading" color="warning" size="small" variant="text" icon="mdi-close-circle-outline">
                    </v-btn>
                </template>
                Clear Data
            </v-tooltip>
            -->
            
            <v-divider vertical inset class="mx-2"></v-divider>
            
            <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                    <v-btn href="/auth/logout" v-bind="props" color="grey" size="small" variant="text" icon="mdi-logout">
                    </v-btn>
                </template>
                Logout
            </v-tooltip>
        </v-sheet>
    </v-sheet>
    
    <v-tabs v-model="tab" density="compact" color="primary">
        <v-tab value="takeDamage">Take Dmg</v-tab>
        <v-tab value="applyDamageByEntity">By Entity</v-tab>
        <v-tab value="applyDamageBySkill">By Skill</v-tab>
        <v-tab value="applyDamageAnalysis">Analysis</v-tab>
        <v-tab value="leaderboard" v-if="currentUser?.leaderboardEnabled">Leaderboard</v-tab>
        <v-tab value="entityList">Entities</v-tab>
        <v-tab value="items">Items</v-tab>

        <v-tab value="logs" v-if="(currentUser?.role || 0) >= 2">Logs</v-tab>
        <v-tab value="configuration" v-if="(currentUser?.role || 0) >= 3">Config</v-tab>
        <v-tab value="admin" v-if="(currentUser?.role || 0) >= 3 && currentUser?.id !== 'local'">Admin</v-tab>
    </v-tabs>
    
    <!-- Historical Session Indicator -->
    <v-alert v-if="currentSessionName" density="compact" color="info" variant="tonal" class="ma-2" border="start" closable @click:close="clearData">
        <template v-slot:prepend>
            <v-icon icon="mdi-history"></v-icon>
        </template>
        Viewing Historical Session: <strong>{{ currentSessionName }}</strong>
        <template v-slot:append>
            <v-btn size="small" variant="flat" color="info" @click="clearData" class="ml-4">
                Return to Live
            </v-btn>
        </template>
    </v-alert>

    <v-tabs-window v-model="tab" class="fill-height">
        <v-tabs-window-item value="takeDamage">
            <take-damage />
        </v-tabs-window-item>

        <v-tabs-window-item value="applyDamageByEntity">
            <apply-damage-by-entity />
        </v-tabs-window-item>

        <v-tabs-window-item value="applyDamageBySkill">
            <apply-damage-by-skill />
        </v-tabs-window-item>

        <v-tabs-window-item value="applyDamageAnalysis">
            <apply-damage-analysis />
        </v-tabs-window-item>

        <v-tabs-window-item value="leaderboard">
            <leaderboard />
        </v-tabs-window-item>

        <v-tabs-window-item value="entityList">
            <entities-tab />
        </v-tabs-window-item>

        <v-tabs-window-item value="items">
            <item-index />
        </v-tabs-window-item>

        <v-tabs-window-item value="logs" v-if="(currentUser?.role || 0) >= 2">
            <log-list @load-session="loadSession" />
        </v-tabs-window-item>

        <v-tabs-window-item value="configuration" v-if="(currentUser?.role || 0) >= 3">
            <configuration />
        </v-tabs-window-item>
        
        <v-tabs-window-item value="admin" v-if="(currentUser?.role || 0) >= 3 && currentUser?.id !== 'local'">
            <Admin />
        </v-tabs-window-item>
    </v-tabs-window>

    <v-dialog v-model="showCaptureSettings" max-width="500">
        <v-card>
            <v-toolbar color="primary" density="compact">
                <v-toolbar-title>Capture Settings</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon="mdi-close" variant="text" @click="showCaptureSettings = false"></v-btn>
            </v-toolbar>
            <v-card-text class="pt-4">
                <NicSetup @done="showCaptureSettings = false" />
            </v-card-text>
        </v-card>
    </v-dialog>

    <v-snackbar v-model="loadingState.active" :timeout="-1" color="blue-grey-darken-4" location="bottom right"
        min-width="300">
        <div class="d-flex flex-column">
            <div class="font-weight-bold mb-2">{{ loadingState.message }}</div>
            <v-progress-linear indeterminate color="primary" height="4" rounded></v-progress-linear>
            <div class="text-caption text-right mt-1" v-if="loadingState.totalBytes > 0">
                {{ (loadingState.loadedBytes / 1024 / 1024).toFixed(1) }} / {{ (loadingState.totalBytes / 1024 /
                    1024).toFixed(1) }} MB
            </div>
            <div class="text-caption text-right mt-1" v-else>
                {{ (loadingState.loadedBytes / 1024 / 1024).toFixed(1) }} MB
            </div>
        </div>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, inject, ref, Ref, nextTick, computed, watch } from "vue";
import LogParserWorker from '@/workers/logParser.worker?worker';

import { SocketClient } from '@/socketClient';
import { eventBase } from "./protocols";
import { trackedStatuses, skillClasses, anonymizeMode, resourcesReady, resourceLoadingProgress, resetStore, statusGroups, weaponNameMap, currentUser, isAuthenticated, analysisFilters, trackedBosses, liveSyncState, skillAmpConfig } from "@/store";
import { loadConfig } from "@/apicall";
import { getStatusIconUrl } from "@/util";

import TakeDamageComponent from '@/components/takeDamage.vue';
import ApplyDamageByEntityComponent from '@/components/applyDamageByEntity.vue';
import ApplyDamageBySkillComponent from '@/components/applyDamageBySkill.vue';
import ApplyDamageAnalysisComponent from '@/components/applyDamageAnalysis.vue';
import LeaderboardComponent from '@/components/Leaderboard.vue';
import EntitiesTabComponent from "./components/EntitiesTab.vue";
import DpsGraphComponent from "./components/DpsGraph.vue";
import LogListComponent from "./components/logList.vue";
import ConfigurationComponent from "./components/Configuration.vue";
import Login from '@/components/Login.vue';
import Admin from '@/components/Admin.vue';
import NicSetup from '@/components/NicSetup.vue';
import ItemIndexComponent from '@/components/ItemIndex.vue';


export default defineComponent({
    name: "App",
    components: {
        TakeDamage: TakeDamageComponent,
        ApplyDamageByEntity: ApplyDamageByEntityComponent,
        ApplyDamageBySkill: ApplyDamageBySkillComponent,
        ApplyDamageAnalysis: ApplyDamageAnalysisComponent,
        Leaderboard: LeaderboardComponent,
        DpsGraph: DpsGraphComponent,
        EntitiesTab: EntitiesTabComponent,
        LogList: LogListComponent,
        Configuration: ConfigurationComponent,
        Login,
        Admin,
        NicSetup,
        ItemIndex: ItemIndexComponent,
    },
    setup() {
        // ... (Injects and logic) ...
        const isLoading = inject('isLoading') as Ref<boolean>;
        const loadingCount = inject('loadingCount') as Ref<number>;
        const region = inject('region');
        // const lang = inject('lang');
        const regionList = inject('regionList');
        const db = inject('db');
        const raceNameMap = inject('raceNameMap');
        const skillNameMap = inject('skillNameMap');
        const condNameMap = inject('condNameMap');
        const itemNameMap = inject('itemNameMap');
        const appEvent = inject('appEvent');
        const actorManager = inject('actorManager');
        const dcManager = inject('dcManager');
        const currentSessionName = inject('currentSessionName') as Ref<string | undefined>;

        const socketConnected = ref(false);
        const socket = new SocketClient(`/ws`);
        socket.onConnect = isConnected => socketConnected.value = isConnected;

        // Worker for log loading
        const logWorker = new LogParserWorker();

        // Worker message handling
        logWorker.onmessage = (e) => {
            const { type, events, progress, error, loadedBytes, totalBytes } = e.data;
            if (type === 'eventBatch') {
                for (const evt of events) {
                    actorManager.value.onEvent(evt);
                }
            } else if (type === 'progress') {
                // Update non-blocking progress
                loadingState.value.loadedBytes = loadedBytes;
                loadingState.value.totalBytes = totalBytes;
                const loadedMB = (loadedBytes / 1024 / 1024).toFixed(1);
                if (totalBytes > 0) {
                    const totalMB = (totalBytes / 1024 / 1024).toFixed(1);
                    loadingState.value.message = `Loading... ${loadedMB} / ${totalMB} MB`;
                } else {
                    loadingState.value.message = `Loading... ${loadedMB} MB`;
                }
            } else if (type === 'complete') {
                loadingState.value.active = false;
                if (loadingCount.value > 0) loadingCount.value--;
                console.timeEnd('loadLog');
            } else if (type === 'error') {
                loadingState.value.active = false;
                if (loadingCount.value > 0) loadingCount.value--;
                console.error("Worker error:", error);
                alert(`Error loading log: ${error}`);
            }
        };

        const loadingState = ref({
            active: false,
            message: '',
            loadedBytes: 0,
            totalBytes: 0
        });

        const handleHydration = async (payload: any) => {
            liveSyncState.value = payload;
            
            // Hydrate Go payload into ActorManager arrays to power the UI
            const mergeEntity = (id: string, state: any) => {
                let entity = actorManager.value.entityMap[id];
                if (!entity) {
                    actorManager.value.onEntityAppear({
                        Id: state.id || id, EventId: 1, At: state.appearAt || 0, Name: state.name || `unknown:${id}`,
                        RaceId: state.raceId || (state.isPC ? 10002 : 10001),
                        Height: 1, Weight: 1, Upper: 1, Lower: 1, GuildName: "", OwnerId: state.ownerId || ""
                    });
                    entity = actorManager.value.entityMap[id];
                }
                if (entity) {
                    entity.aggregatedStats = state.aggregatedStats || {};
                    (entity as any).weaponUsageByClass = state.weaponUsage || {};
                    (entity as any).statusTimelines = state.statusTimelines || {};
                    let payloadEquipMap: Record<number, any> = {};
                    if (state.equipItemMap) {
                        for (const pocketStr in state.equipItemMap) {
                            payloadEquipMap[Number(pocketStr)] = { 
                                PocketType: Number(pocketStr), 
                                ItemId: state.equipItemMap[pocketStr] 
                            };
                        }
                    }
                    (entity as any)._equipItemMap = payloadEquipMap;
                    (entity as any).aggregatedTakeStats = state.aggregatedTakeStats || {};
                    (entity as any).hpHistory = state.hpHistory || [];
                    (entity as any)._statusStarts = state.statusStarts || {};
                    (entity as any).statusStats = state.statusStats || null;
                    (entity as any)._totalTakeDamage = state.totalTakeDamage || 0;
                    (entity as any)._totalApplyDamage = state.totalApplyDamage || 0;
                    (entity as any)._disappearAt = state.disappearAt || 0;
                    (entity as any)._lastTakeDamageAt = state.lastTakeDamageAt || 0;
                    // Override _appearAt unconditionally
                    if (state.appearAt) (entity as any)._appearAt = state.appearAt;
                }
            };

            const players = Object.entries(payload.playerStats || {});
            const targets = Object.entries(payload.targetStats || {});
            const total = players.length + targets.length;
            let current = 0;

            const CHUNK_SIZE = 100;
            const yieldToBrowser = () => new Promise(resolve => setTimeout(resolve, 0));

            for (const [id, state] of players) {
                mergeEntity(id, state);
                current++;
                if (current % CHUNK_SIZE === 0) {
                    loadingState.value.message = `Hydrating players... (${current}/${total})`;
                    await yieldToBrowser();
                }
            }

            for (const [id, state] of targets) {
                mergeEntity(id, state);
                current++;
                if (current % CHUNK_SIZE === 0) {
                    loadingState.value.message = `Hydrating entities... (${current}/${total})`;
                    await yieldToBrowser();
                }
            }
            
            // Force pure reactivity update once at the end
            actorManager.value.liveUpdate.value++;
            
            // Refinement 151: Removed redundant per-entity vueUpdateRequest loop.
            // liveUpdate.value++ is already triggering computed updates correctly.
        };

        socket.onEvent = (event) => {
            if (event.EventId === 9999) {
                console.log("[WS] Received System Progress:", event);
                const sysEvent = event as any;
                if (sysEvent.Type === "ReplayProgress") {
                    loadingState.value.active = true;
                    loadingState.value.loadedBytes = sysEvent.Current;
                    loadingState.value.totalBytes = sysEvent.Total;
                    const loadedMB = (sysEvent.Current / 1024 / 1024).toFixed(1);
                    const totalMB = (sysEvent.Total / 1024 / 1024).toFixed(1);
                    loadingState.value.message = `Replaying ${sysEvent.Message}... ${loadedMB} / ${totalMB} MB`;

                    if (sysEvent.Current >= sysEvent.Total) {
                        setTimeout(() => {
                            loadingState.value.active = false;
                            window.dispatchEvent(new CustomEvent('replay-finished'));
                        }, 1000);
                    }
                }
                return;
            } 
            
            // A new live session started (Analysis tab's New Session): the live
            // view starts over; the entities still in view follow this event.
            if (event.EventId === 9997) {
                if (!currentSessionName.value) resetStore();
                return;
            }

            // Check if we are viewing a historical log or DB session
            // If so, MUTE the live websocket events to prevent data corruption.
            if (currentSessionName.value) {
                return;
            }

            if (event.EventId === 9998) {
                handleHydration(event);
                return;
            }
            actorManager.value.onEvent(event);
        };

        // Compatibility shim for loadingMessage
        const loadingMessage = computed({
            get: () => {
                if (loadingState.value.active) return loadingState.value.message;
                return loadingCount.value > 0 ? "Loading..." : "";
            },
            set: (v) => {
                loadingState.value.message = v;
                if (v) loadingState.value.active = true;
            }
        });



        const clearData = async () => {
            loadingMessage.value = "";

            // Note: We no longer reset the backend automatically here.
            // This allows a "Live" capture to continue in the background while 
            // the user is reviewing a historical log. The UI will stay isolated
            // because App.vue's socket.onEvent skips live updates if currentSessionName is set.

            // Full store reset (Refinement 120)
            resetStore();
        }

        const download = () => {
            // Recheck permission just in case
            if ((currentUser.value?.role || 0) < 2) return;

            const anonQuery = anonymizeMode.value ? '&anonymize=true' : '';

            let url: string;
            let filename: string;
            if (currentSessionName.value) {
                url = `/api/packet_log?session=${currentSessionName.value}${anonQuery}`;
                filename = `${currentSessionName.value}.ndjson`;
            } else {
                const baseQuery = anonymizeMode.value ? '?anonymize=true' : '';
                url = `/api/packet_log${baseQuery}`;
                filename = `session.ndjson`;
            }

            // Use a programmatic anchor to trigger a real browser download dialog,
            // bypassing Chrome's popup blocker that silently kills window.open().
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }



        const loadFromFile = async () => {
            // Auto-clear if something is already loaded
            if (currentSessionName.value) {
                await clearData();
                await nextTick();
            }

            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.ndjson';

            input.onchange = async () => {
                if (!input.files?.length) return;
                const file = input.files[0];
                currentSessionName.value = file.name;
                console.time('loadLog');

                loadingState.value = {
                    active: true,
                    message: "Starting local load...",
                    loadedBytes: 0,
                    totalBytes: file.size
                };

                loadingCount.value++;
                // Start background resource loading

                logWorker.postMessage({ type: 'loadFile', id: 'file', file });
            };

            input.click();
        }

        const loadSession = async (tableName: string, totalEventsHint?: number, isLive: boolean = false) => {
             if ((currentUser.value?.role || 0) < 1) return; // RoleUser+ allowed

            // Always clear first, even coming from the live view (currentSessionName unset):
            // otherwise the actorManager still holds whatever the live websocket has been
            // accumulating, and handleHydration below only merges the new session's entities
            // on top of it instead of replacing it, leaving stale live data mixed into the view.
            await clearData();
            await nextTick();

            try {
                if (!isLive) {
                    currentSessionName.value = tableName;
                }
                console.time('loadLog');
                loadingState.value = {
                    active: true,
                    message: `Loading session ${tableName} via API...`,
                    loadedBytes: 0,
                    totalBytes: 0
                };

                loadingCount.value++;
                
                // Fetch the aggregated matrix directly from the Go API
                const resp = await fetch(`/api/session/analysis?target=${tableName}`);
                if (!resp.ok) {
                    throw new Error("Failed to load generic session view: " + await resp.text());
                }
                
                const aggregatedJson = await resp.json();
                await handleHydration(aggregatedJson);
                
                loadingState.value.active = false;
                if (loadingCount.value > 0) loadingCount.value--;
                console.timeEnd('loadLog');
                
                tab.value = "applyDamageAnalysis";
            } catch (e) {
                console.error(e);
                loadingState.value.active = false;
                if (loadingCount.value > 0) loadingCount.value--;
            }
        };

        const loadFromServer = async () => {
            try {
                isLoading.value = true;
                // Only the active session is needed: a one-row page carries it
                // without the server counting every session table.
                const res = await fetch(`/api/sessions?page=1&pageSize=1&_t=${Date.now()}`);
                if (!res.ok) throw new Error("Failed to fetch sessions");
                const data = await res.json();

                const activeSession = data?.active;
                if (activeSession) {
                    await loadSession(activeSession.tableName, undefined, true);
                    // Switch to logs tab to show the list? Or stay on current tab?
                    // User probably wants to see data.
                    // loadSession already switches to applyDamageAnalysis
                } else {
                    alert("No active session found.");
                }
            } catch (e) {
                console.error(e);
                alert("Failed to load active session");
            } finally {
                isLoading.value = false;
            }
        };

        const loadResources = () => {
            // Initial State
            resourcesReady.value = false;
            resourceLoadingProgress.value = 0;

            console.log('[App] Starting background resource load...');

            // Sequence heavy loads or parallel with progress tracking
            // Since we can't easily track XHR progress from DB wrapper, we'll simulate steps

            // Parallel Loading Steps
            const steps = [
                { task: db.value.getData('SkillNameMap'), name: 'Skills', weight: 40 },
                { task: db.value.getData('StatusNameMap'), name: 'Statuses', weight: 30 },
                { task: db.value.getData('RaceNameMap'), name: 'Races', weight: 20 },
                // Image Preloading Task (Created dynamically below)
            ];

            // Image Preloader logic
            const preloadImagesTask = async () => {
                const statusIds = trackedStatuses.value || [];
                if (statusIds.length === 0) return {}; // Return empty object instead of null to match type

                const promises = statusIds.map(id => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.src = getStatusIconUrl(id);
                        img.onload = () => resolve(true);
                        img.onerror = () => resolve(false); // Resolve even on error to not block app
                    });
                });

                // Allow some concurrency but don't strictly wait forever if one hangs? 
                // Promise.all waits effectively.
                await Promise.all(promises);
                return {}; // Return meaningful data or empty object
            };

            steps.push({ task: preloadImagesTask(), name: 'Images', weight: 10 });

            let loadedWeight = 0;

            // Execute in parallel but track completion
            Promise.all(steps.map(step => {
                return step.task.then((data: any) => {
                    loadedWeight += step.weight;
                    resourceLoadingProgress.value = loadedWeight;
                    return { name: step.name, data };
                });
            })).then((results) => {
                // Apply data
                results.forEach(r => {
                    if (r.name === 'Skills' && r.data) skillNameMap.value = r.data;
                    if (r.name === 'Statuses' && r.data) condNameMap.value = r.data;
                    if (r.name === 'Races' && r.data) raceNameMap.value = r.data;
                });

                console.log('[App] Background resources and images loaded fully');

                // Small delay to let UI smooth out
                setTimeout(() => {
                    resourcesReady.value = true;
                }, 500);
            }).catch((e: any) => {
                console.error("Failed to load background resources", e);
                // Even on error, we should probably unblock, or show error state
                resourcesReady.value = true; // Unblock to allow basic usage?
            });
        };

        const tab = ref('');

        const loadingAuth = ref(true);
        const nicConfigured = ref(true); // optimistic; corrected after auth check
        const showCaptureSettings = ref(false);
        let hasInitialized = false;

         const initializeApp = async () => {
            if (hasInitialized) return;
            hasInitialized = true;
            // Load persistent config
            try {
                const cfg = await loadConfig<number[]>("tracked_statuses");
                if (cfg) {
                    trackedStatuses.value = cfg;
                }
            } catch (e) {
                console.error("Failed to load config", e);
            }

            try {
                const bossCfg = await loadConfig<number[]>("tracked_bosses");
                if (bossCfg) {
                    trackedBosses.value = bossCfg;
                }
            } catch (e) {
                console.error("Failed to load boss config", e);
            }

            try {
                const classCfg = await loadConfig<any[]>("skill_classes");
                if (classCfg) {
                    skillClasses.value = classCfg;
                }
            } catch (e) {
                console.error("Failed to load class config", e);
            }

            try {
                const ampCfg = await loadConfig<any[]>("skill_amplification");
                if (ampCfg) {
                    skillAmpConfig.value = ampCfg;
                }
            } catch (e) {
                console.error("Failed to load skill amplification config", e);
            }

            try {
                const groupsCfg = await loadConfig<any[]>("status_groups");
                if (groupsCfg) {
                    statusGroups.value = groupsCfg;
                }
            } catch (e) {
                console.error("Failed to load status groups config", e);
            }

            try {
                const filtersCfg = await loadConfig<any>("analysis_filters");
                if (filtersCfg) {
                    analysisFilters.value = { ...analysisFilters.value, ...filtersCfg };
                }
            } catch (e) {
                console.error("Failed to load analysis filters config", e);
            }

            // Load weapon names from API
            try {
                const weaponResp = await fetch('/api/resources/weapons');
                const weaponData = await weaponResp.json();
                weaponNameMap.value = weaponData;
                console.log("Loaded weapons:", Object.keys(weaponNameMap.value).length);
            } catch (e) {
                console.error("Failed to load weapon names", e);
            }
            
            await db.value.tryOpen();
            // Preload resources for instant UI
            loadResources();

            socket.connect();
         }

        onMounted(async () => {
            regionList.value = ['kr', 'krt', 'cn', 'jp', 'tw', 'us'];

            try {
                const res = await fetch('/api/me');
                if (res.ok) {
                    currentUser.value = await res.json();
                } else {
                    currentUser.value = null;
                }
            } catch (e) {
                 currentUser.value = null;
            } finally {
                loadingAuth.value = false;
            }

            // Always check NIC config — in local mode Login.vue is never shown
            try {
                const nicRes = await fetch('/api/nic/config');
                if (nicRes.ok) {
                    const nicData = await nicRes.json();
                    nicConfigured.value = nicData.is_configured;
                }
            } catch (e) {
                console.error('Failed to check NIC config', e);
            }

            if (isAuthenticated.value && (currentUser.value?.role || 0) > 0) {
                await initializeApp();
            }
        });
        
        watch(currentUser, async (user) => {
            if (user && user.role > 0 && !socketConnected.value) {
                await initializeApp();
            }
        });

        const getRoleName = (role: number | undefined) => {
            switch (role) {
                case 0: return 'Pending';
                case 1: return 'User';
                case 2: return 'Advanced';
                case 3: return 'Admin';
                default: return 'Unknown';
            }
        }

        return {
            isLoading,
            region,

            socketConnected,
            clearData,
            download,
            loadFromFile,
            loadFromServer,
            loadSession,
            anonymizeMode,

            tab,
            loadingMessage,
            loadingState,
            currentSessionName,
            resourcesReady,
            resourceLoadingProgress,
            
            currentUser,
            isAuthenticated,
            loadingAuth,
            nicConfigured,
            showCaptureSettings,
            getRoleName
        }
    }
});

</script>
